import { computed, watch } from "vue";
import { useStore } from "vuex";

import { defineStore } from "pinia";

import {
  AnalyticsService,
  FullStoryAnalyticsProvider,
  PostHogAnalyticsProvider,
} from "@lendflow/utils/analytics";

import { formatUserFullName } from "@/helpers/formatting";

import type { AnalyticsEvent } from "@/enums/analytics";
import type { IClient } from "@/models/clients";
import type { IUser } from "@/models/users";

const analyticsService = new AnalyticsService([
  new FullStoryAnalyticsProvider(),
  new PostHogAnalyticsProvider(),
]);

export const useAnalyticsStore = defineStore("analytics", () => {
  const { getters } = useStore();

  const user = computed<IUser | null>(() => {
    return getters["auth/user"];
  });

  const userClientData = computed<IClient | null>(
    () => getters["auth/authClientSettings"]
  );

  const impersonatorId = computed<number | null>(
    () => getters["auth/impersonatorId"] ?? null
  );

  watch(user, (value) => {
    const payload = value
      ? {
          id: value.id,
          displayName:
            value.full_name ?? `${value.first_name} ${value.last_name}`,
          email: value.email ?? "",
          roles: value.roles ?? [],
          clientId: value.client_id,
        }
      : null;
    analyticsService.trackUserChange(payload);
  });

  watch(userClientData, (clientData) => {
    const clientDataPayload = {
      clientName: clientData?.name ?? "Client data empty.",
      clientId: clientData?.id ?? "Client data empty.",
    };
    analyticsService.trackUserPropertiesChange(clientDataPayload);
  });

  const trackEvent = (
    event: AnalyticsEvent,
    properties?: Record<string, unknown>
  ) => {
    analyticsService.trackEvent(event, {
      ...properties,
      user_name: formatUserFullName(user.value),
      impersonated_by: impersonatorId.value,
    });
  };

  return {
    trackEvent,
    trackException: analyticsService.trackException.bind(analyticsService),
  };
});
